/**
 * 記入書類 — 管理者向け一覧・追加・編集・PDFテンプレートバージョン管理
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { API_BASE_URL } from '../../../../../config';
import type { FormToFillRecord } from './types';
import FormTemplateModal from './FormTemplateModal';
import '../variousCopiesUpload/VariousCopiesUpload.css';

interface ApiFormToFillRow {
  formCode: string;
  nameJa: string;
  nameEn?: string | null;
  nameVi?: string | null;
  descriptionJa?: string | null;
  descriptionEn?: string | null;
  descriptionVi?: string | null;
  supportsTemplateUpload?: boolean;
}

function mapApiToRecord(row: ApiFormToFillRow): FormToFillRecord {
  return {
    formCode: row.formCode,
    nameJa: row.nameJa ?? '',
    nameEn: row.nameEn ?? '',
    nameVi: row.nameVi ?? '',
    descriptionJa: row.descriptionJa ?? '',
    descriptionEn: row.descriptionEn ?? '',
    descriptionVi: row.descriptionVi ?? '',
    supportsTemplateUpload: row.supportsTemplateUpload === true,
  };
}

function buildCreateBody(payload: FormState): Record<string, unknown> {
  return {
    nameJa: payload.nameJa.trim(),
    nameEn: payload.nameEn.trim() || null,
    nameVi: payload.nameVi.trim() || null,
    descriptionJa: payload.descriptionJa.trim() || null,
    descriptionEn: payload.descriptionEn.trim() || null,
    descriptionVi: payload.descriptionVi.trim() || null,
  };
}

async function parseErrorMessage(res: Response): Promise<string> {
  try {
    const data = await res.json();
    if (data && typeof data.message === 'string') return data.message;
  } catch {
    /* ignore */
  }
  if (res.status === 400) return '入力内容を確認してください（日本語の書類名は必須です）。';
  if (res.status === 404) return '対象のデータが見つかりません。';
  return `リクエストに失敗しました（${res.status}）。`;
}

type ModalMode = 'add' | 'edit';

interface FormState {
  nameJa: string;
  nameEn: string;
  nameVi: string;
  descriptionJa: string;
  descriptionEn: string;
  descriptionVi: string;
}

function recordToForm(r: FormToFillRecord): FormState {
  return {
    nameJa: r.nameJa,
    nameEn: r.nameEn,
    nameVi: r.nameVi,
    descriptionJa: r.descriptionJa,
    descriptionEn: r.descriptionEn,
    descriptionVi: r.descriptionVi,
  };
}

function emptyForm(): FormState {
  return {
    nameJa: '',
    nameEn: '',
    nameVi: '',
    descriptionJa: '',
    descriptionEn: '',
    descriptionVi: '',
  };
}

const INITIAL_ADD_FORM = emptyForm();

interface FormToFillModalProps {
  isOpen: boolean;
  mode: ModalMode;
  formCode?: string | null;
  initial: FormState;
  onClose: () => void;
  onSave: (payload: FormState) => Promise<void>;
}

const FormToFillModal: React.FC<FormToFillModalProps> = ({
  isOpen,
  mode,
  formCode,
  initial,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState<FormState>(initial);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(initial);
      setSubmitting(false);
    }
  }, [isOpen, initial]);

  if (!isOpen) return null;

  const title = mode === 'add' ? '記入書類を追加' : '記入書類を編集';
  const canSave = form.nameJa.trim().length > 0 && !submitting;

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!canSave || submitting) return;
    setSubmitting(true);
    try {
      await onSave({
        ...form,
        nameJa: form.nameJa.trim(),
        nameEn: form.nameEn.trim(),
        nameVi: form.nameVi.trim(),
        descriptionJa: form.descriptionJa.trim(),
        descriptionEn: form.descriptionEn.trim(),
        descriptionVi: form.descriptionVi.trim(),
      });
      onClose();
    } catch {
      /* parent shows error */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="vc-upload-modal-overlay" onClick={submitting ? undefined : onClose} role="dialog" aria-modal="true" aria-labelledby="ftf-upload-modal-title">
      <div className="vc-upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vc-upload-modal__header">
          <h2 id="ftf-upload-modal-title" className="vc-upload-modal__title">
            {title}
          </h2>
          <button type="button" className="vc-upload-modal__close" onClick={onClose} aria-label="閉じる" disabled={submitting}>
            ×
          </button>
        </div>

        <div className="vc-upload-modal__body">
          {mode === 'edit' && formCode && (
            <p className="vc-upload-modal__readonly">
              書類コード（変更不可）: <code>{formCode}</code>
            </p>
          )}
          <p className="vc-upload-modal__section-title">書類名</p>
          <div className="vc-upload-modal__field">
            <label className="vc-upload-modal__label">
              日本語 <span className="vc-upload-modal__req">*</span>
            </label>
            <input
              type="text"
              className="vc-upload-modal__input"
              value={form.nameJa}
              onChange={(e) => update('nameJa', e.target.value)}
              placeholder="例：労働者名簿"
              autoComplete="off"
              disabled={submitting}
            />
          </div>
          <div className="vc-upload-modal__field">
            <label className="vc-upload-modal__label">English</label>
            <input
              type="text"
              className="vc-upload-modal__input"
              value={form.nameEn}
              onChange={(e) => update('nameEn', e.target.value)}
              placeholder="例：Employee register"
              autoComplete="off"
              disabled={submitting}
            />
          </div>
          <div className="vc-upload-modal__field">
            <label className="vc-upload-modal__label">Tiếng Việt</label>
            <input
              type="text"
              className="vc-upload-modal__input"
              value={form.nameVi}
              onChange={(e) => update('nameVi', e.target.value)}
              autoComplete="off"
              disabled={submitting}
            />
          </div>

          <p className="vc-upload-modal__section-title">説明・指示（従業員向けチャット等に表示）</p>
          <div className="vc-upload-modal__field">
            <label className="vc-upload-modal__label">日本語</label>
            <textarea
              className="vc-upload-modal__textarea"
              value={form.descriptionJa}
              onChange={(e) => update('descriptionJa', e.target.value)}
              placeholder="記入時の注意事項など"
              rows={3}
              disabled={submitting}
            />
          </div>
          <div className="vc-upload-modal__field">
            <label className="vc-upload-modal__label">English</label>
            <textarea
              className="vc-upload-modal__textarea"
              value={form.descriptionEn}
              onChange={(e) => update('descriptionEn', e.target.value)}
              placeholder="Instructions for employees"
              rows={3}
              disabled={submitting}
            />
          </div>
          <div className="vc-upload-modal__field">
            <label className="vc-upload-modal__label">Tiếng Việt</label>
            <textarea
              className="vc-upload-modal__textarea"
              value={form.descriptionVi}
              onChange={(e) => update('descriptionVi', e.target.value)}
              placeholder="Hướng dẫn cho nhân viên"
              rows={3}
              disabled={submitting}
            />
          </div>
        </div>

        <div className="vc-upload-modal__footer">
          <div className="vc-upload-modal__footer-right">
            <button type="button" className="vc-upload-modal__btn-cancel" onClick={onClose} disabled={submitting}>
              キャンセル
            </button>
            <button type="button" className="vc-upload-modal__btn-save" onClick={() => void handleSave()} disabled={!canSave}>
              {submitting ? '処理中…' : mode === 'add' ? '追加' : '保存'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const FormsToFillUpload: React.FC = () => {
  const [rows, setRows] = useState<FormToFillRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('add');
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [templateFormCode, setTemplateFormCode] = useState<string | null>(null);

  const editingRecord = useMemo(
    () => (editingCode ? rows.find((r) => r.formCode === editingCode) ?? null : null),
    [rows, editingCode]
  );

  const modalInitial: FormState = useMemo(() => {
    if (modalMode === 'edit' && editingRecord) return recordToForm(editingRecord);
    return INITIAL_ADD_FORM;
  }, [modalMode, editingRecord]);

  const loadRows = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/forms-to-fill`);
      if (!res.ok) {
        throw new Error(await parseErrorMessage(res));
      }
      const data: ApiFormToFillRow[] = await res.json();
      setRows(Array.isArray(data) ? data.map(mapApiToRecord) : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : '一覧の取得に失敗しました。');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const openEdit = useCallback((formCode: string) => {
    setEditingCode(formCode);
    setModalMode('edit');
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingCode(null);
  }, []);

  const handleSave = useCallback(
    async (payload: FormState) => {
      if (!editingCode) return;
      const body = buildCreateBody(payload);
      const res = await fetch(`${API_BASE_URL}/api/forms-to-fill/${encodeURIComponent(editingCode)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await parseErrorMessage(res));
      await loadRows();
    },
    [editingCode, loadRows]
  );

  const templateRecord = useMemo(
    () => (templateFormCode ? rows.find((r) => r.formCode === templateFormCode) ?? null : null),
    [rows, templateFormCode]
  );

  const displayCell = (text: string) => (text.trim() ? text : <span className="vc-upload__cell-muted">—</span>);

  return (
    <div className="vc-upload">
      <div className="vc-upload__header-row">
        <div className="vc-upload__title-block">
          <p className="vc-upload__intro">
            従業員が Web 上で記入する「記入書類」の名称・説明を編集できます。書類コード（FF###）は変更できません。
            PDFプレビュー対応の書類はテンプレートの新バージョンをアップロード・検証してから有効化できます。
          </p>
        </div>
      </div>

      {error && (
        <p className="vc-upload__error" role="alert">
          {error}
        </p>
      )}

      <div className="vc-upload__table-wrap">
        {loading ? (
          <p className="vc-upload__loading">読み込み中…</p>
        ) : rows.length === 0 ? (
          <p className="vc-upload__empty">登録された記入書類はありません。</p>
        ) : (
          <table className="vc-upload__table">
            <thead>
              <tr>
                <th className="vc-upload__th-index">番号</th>
                <th>コード</th>
                <th>書類名（日本語）</th>
                <th>書類名（English）</th>
                <th>書類名（Tiếng Việt）</th>
                <th className="vc-upload__th-actions">操作</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const n = index + 1;
                return (
                  <tr key={row.formCode}>
                    <td className="vc-upload__td-index">{n}</td>
                    <td>
                      <code className="ftf-form-code">{row.formCode}</code>
                    </td>
                    <td>{displayCell(row.nameJa)}</td>
                    <td>{displayCell(row.nameEn)}</td>
                    <td>{displayCell(row.nameVi)}</td>
                    <td className="vc-upload__cell-actions">
                      <button type="button" className="vc-upload__btn-link" onClick={() => openEdit(row.formCode)}>
                        編集
                      </button>
                      {row.supportsTemplateUpload && (
                        <button
                          type="button"
                          className="vc-upload__btn-link"
                          onClick={() => setTemplateFormCode(row.formCode)}
                        >
                          PDFテンプレート
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <FormToFillModal
        key={modalOpen ? `${modalMode}-${editingCode ?? 'new'}` : 'closed'}
        isOpen={modalOpen}
        mode={modalMode}
        formCode={editingCode}
        initial={modalInitial}
        onClose={closeModal}
        onSave={async (payload) => {
          setError(null);
          try {
            await handleSave(payload);
          } catch (e) {
            const msg = e instanceof Error ? e.message : '保存に失敗しました。';
            setError(msg);
            throw e;
          }
        }}
      />

      {templateRecord && (
        <FormTemplateModal
          isOpen={templateFormCode != null}
          formCode={templateRecord.formCode}
          formNameJa={templateRecord.nameJa}
          onClose={() => setTemplateFormCode(null)}
        />
      )}
    </div>
  );
};

export default FormsToFillUpload;
